export interface TineDef {
  id: string; // Note name (e.g., "C4", "F#5")
  freq: number;
  keys: string[];
  row: "left" | "center" | "right";
  index: number; // 0-16 spatial index (Left -> Right)
}

// Helper to create a tine definition
const t = (
  note: string,
  freq: number,
  keys: string[],
  row: "left" | "center" | "right",
  index: number
): TineDef => ({ id: note, freq, keys, row, index });

// Spatial layout remains constant
const LAYOUT = [
  { keys: ["q"], row: "left" as const, index: 0 },
  { keys: ["w"], row: "left" as const, index: 1 },
  { keys: ["e"], row: "left" as const, index: 2 },
  { keys: ["r"], row: "left" as const, index: 3 },
  { keys: ["a", "t"], row: "left" as const, index: 4 }, // C5 mapped to A and T
  { keys: ["s"], row: "left" as const, index: 5 },
  { keys: ["d"], row: "left" as const, index: 6 },
  { keys: ["f"], row: "left" as const, index: 7 },
  { keys: ["g", " "], row: "center" as const, index: 8 }, // Center mapped to G and Space
  { keys: ["h"], row: "right" as const, index: 9 },
  { keys: ["j"], row: "right" as const, index: 10 },
  { keys: ["k"], row: "right" as const, index: 11 },
  { keys: ["l", "y"], row: "right" as const, index: 12 }, // D5 mapped to L and Y
  { keys: ["u"], row: "right" as const, index: 13 },
  { keys: ["i"], row: "right" as const, index: 14 },
  { keys: ["o"], row: "right" as const, index: 15 },
  { keys: ["p"], row: "right" as const, index: 16 },
];

// Note Frequencies
const NOTES: Record<string, number> = {
  C4: 261.63, Db4: 277.18, D4: 293.66, Eb4: 311.13, E4: 329.63, F4: 349.23, Fs4: 369.99, G4: 392.00, Ab4: 415.30, A4: 440.00, Bb4: 466.16, B4: 493.88,
  C5: 523.25, Db5: 554.37, D5: 587.33, Eb5: 622.25, E5: 659.25, F5: 698.46, Fs5: 739.99, G5: 783.99, Ab5: 830.61, A5: 880.00, Bb5: 932.33, B5: 987.77,
  C6: 1046.50, Db6: 1108.73, D6: 1174.66, Eb6: 1244.51, E6: 1318.51, F6: 1396.91, Fs6: 1479.98, G6: 1567.98,
  // Extra lows for mapping convenience if needed
  A3: 220.00, B3: 246.94
};

// Tuning Generators
// The V-Shape mapping logic:
// Center: 0
// Left 1: 1, Left 2: 3, Left 3: 5...
// Right 1: 2, Right 2: 4, Right 3: 6...
// Wait, the standard array order in V-shape is alternating.
// 17 notes scale array: [Root, 2nd, 3rd, 4th, 5th, 6th, 7th, 8th(Oct), ...]
// Map to physical layout:
// Scale[0] -> Center (Index 8)
// Scale[1] -> Left 1 (Index 7)
// Scale[2] -> Right 1 (Index 9)
// Scale[3] -> Left 2 (Index 6)
// Scale[4] -> Right 2 (Index 10)
// ...

function mapScaleToTines(scaleNotes: { note: string; freq: number }[]): TineDef[] {
  const mappingOrder = [8, 7, 9, 6, 10, 5, 11, 4, 12, 3, 13, 2, 14, 1, 15, 0, 16];
  
  return mappingOrder.map((layoutIndex, i) => {
    const noteData = scaleNotes[i];
    const layout = LAYOUT[layoutIndex];
    return t(noteData.note, noteData.freq, layout.keys, layout.row, layout.index);
  }).sort((a, b) => a.index - b.index); // Sort by spatial index for consistency
}

// 1. C Major (Standard)
const C_MAJOR_NOTES = [
  { note: "C4", freq: NOTES.C4 }, { note: "D4", freq: NOTES.D4 }, { note: "E4", freq: NOTES.E4 },
  { note: "F4", freq: NOTES.F4 }, { note: "G4", freq: NOTES.G4 }, { note: "A4", freq: NOTES.A4 },
  { note: "B4", freq: NOTES.B4 }, { note: "C5", freq: NOTES.C5 }, { note: "D5", freq: NOTES.D5 },
  { note: "E5", freq: NOTES.E5 }, { note: "F5", freq: NOTES.F5 }, { note: "G5", freq: NOTES.G5 },
  { note: "A5", freq: NOTES.A5 }, { note: "B5", freq: NOTES.B5 }, { note: "C6", freq: NOTES.C6 },
  { note: "D6", freq: NOTES.D6 }, { note: "E6", freq: NOTES.E6 }
];

// 2. G Major (F -> F#)
const G_MAJOR_NOTES = [
  { note: "G3", freq: 196.00 }, // Usually G-tuned starts lower, but let's map to C-range for simplicity? 
  // Actually, a G-tuned kalimba usually *starts* on G3. 
  // But to keep the "physical" instrument similar, let's just retune the C4 kalimba to G Major scale starting on C? (Locrian?) No, that's weird.
  // Let's stick to C Major Root but with F#. (Lydian mode of C / G Major scale).
  // OR: Authentic G Major tuning: Center is G4.
  // Let's do Authentic G Major: G4, A4, B4, C5, D5, E5, F#5, G5...
  // Range: G4 to B6?
  // Let's keep the pitch range similar. Center G4.
  { note: "G4", freq: NOTES.G4 }, { note: "A4", freq: NOTES.A4 }, { note: "B4", freq: NOTES.B4 },
  { note: "C5", freq: NOTES.C5 }, { note: "D5", freq: NOTES.D5 }, { note: "E5", freq: NOTES.E5 },
  { note: "F#5", freq: NOTES.Fs5 }, { note: "G5", freq: NOTES.G5 }, { note: "A5", freq: NOTES.A5 },
  { note: "B5", freq: NOTES.B5 }, { note: "C6", freq: NOTES.C6 }, { note: "D6", freq: NOTES.D6 },
  { note: "E6", freq: NOTES.E6 }, { note: "F#6", freq: NOTES.Fs6 }, { note: "G6", freq: NOTES.G6 },
  { note: "A6", freq: 1760.00 }, { note: "B6", freq: 1975.53 }
];

// 3. Akebono (Japanese Pentatonic) - C, D, Eb, G, Ab
// C4, D4, Eb4, G4, Ab4, C5, D5, Eb5, G5, Ab5, C6, D6, Eb6, G6, Ab6, C7...
const AKEBONO_NOTES = [
  { note: "C4", freq: NOTES.C4 }, { note: "D4", freq: NOTES.D4 }, { note: "Eb4", freq: NOTES.Eb4 },
  { note: "G4", freq: NOTES.G4 }, { note: "Ab4", freq: NOTES.Ab4 }, { note: "C5", freq: NOTES.C5 },
  { note: "D5", freq: NOTES.D5 }, { note: "Eb5", freq: NOTES.Eb5 }, { note: "G5", freq: NOTES.G5 },
  { note: "Ab5", freq: NOTES.Ab5 }, { note: "C6", freq: NOTES.C6 }, { note: "D6", freq: NOTES.D6 },
  { note: "Eb6", freq: NOTES.Eb6 }, { note: "G6", freq: NOTES.G6 }, { note: "Ab6", freq: 1661.22 },
  { note: "C7", freq: 2093.00 }, { note: "D7", freq: 2349.32 }
];

export const TUNINGS = {
  "c-major": {
    name: "C Major (Standard)",
    tines: mapScaleToTines(C_MAJOR_NOTES)
  },
  "g-major": {
    name: "G Major",
    tines: mapScaleToTines(G_MAJOR_NOTES)
  },
  "akebono": {
    name: "Akebono (Pentatonic)",
    tines: mapScaleToTines(AKEBONO_NOTES)
  }
} as const;

export type TuningType = keyof typeof TUNINGS;
